import React from 'react';
import { useSocket } from '../../contexts/SocketContext';
import { formatString, GameType } from '@deadpan/shared';
import { useTranslations } from '../../hooks/useTranslations';

export const SimilarMindsAnswerInput: React.FC = () => {
  const { socket, gameState, currentRoom, mySimilarMindsAnswerId } = useSocket();
  const t = useTranslations();
  const sm = t.games.similarMinds;
  const [answer, setAnswer] = React.useState('');
  const [isEditingSubmitted, setIsEditingSubmitted] = React.useState(false);
  const [now, setNow] = React.useState(() => Date.now());
  const autoSubmitAttemptedRef = React.useRef(false);

  React.useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 200);
    return () => clearInterval(interval);
  }, []);

  const hasSubmitted = Boolean(mySimilarMindsAnswerId);
  const timer = gameState?.timer;
  const timeRemaining =
    timer && timer.timerEndTime > 0
      ? Math.max(0, Math.ceil((timer.timerEndTime - now) / 1000))
      : null;
  const hasAnswerTimer = timeRemaining !== null;

  React.useEffect(() => {
    if (currentRoom?.phase !== 'fact_input' || currentRoom?.gameType !== GameType.SIMILAR_MINDS) {
      autoSubmitAttemptedRef.current = false;
    }
  }, [currentRoom?.phase, currentRoom?.gameType]);

  React.useEffect(() => {
    if (!hasAnswerTimer || hasSubmitted || autoSubmitAttemptedRef.current) return;
    if (timeRemaining !== 0) return;
    const trimmed = answer.trim();
    if (!socket || !trimmed) return;
    autoSubmitAttemptedRef.current = true;
    socket.emit('client:submit-similar-minds-answer', trimmed);
    setIsEditingSubmitted(false);
  }, [hasAnswerTimer, timeRemaining, hasSubmitted, socket, answer]);

  if (!gameState || !currentRoom) {
    return null;
  }

  const participants = currentRoom.players.filter((p) => !p.isSpectator);
  const submittedCount = (gameState.similarMindsSubmittedPlayerIds ?? []).length;
  const isLockedAfterSubmit = hasSubmitted && !isEditingSubmitted;

  const handleSubmit = () => {
    const trimmed = answer.trim();
    if (!socket || !trimmed || trimmed.length > 200) return;
    socket.emit('client:submit-similar-minds-answer', trimmed);
    setIsEditingSubmitted(false);
  };

  const question = gameState.similarMindsQuestion;

  return (
    <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8 w-[90%] max-w-[600px] border border-white/20">
      {question && (
        <div className="mb-4 p-3 sm:p-4 rounded-xl border-2 border-teal-200 bg-gradient-to-br from-teal-50 to-cyan-50">
          <p className="text-xs sm:text-sm font-bold text-teal-700 mb-1">{sm.questionLabel}</p>
          <p className="text-base sm:text-lg text-gray-900 whitespace-pre-wrap break-words">{question}</p>
        </div>
      )}
      <div className="text-center mb-5">
        <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">
          {sm.submitAnswerTitle}
        </h2>
        <p className="text-sm sm:text-base text-gray-700 mt-2">{sm.submitAnswerInstruction}</p>
        {hasAnswerTimer && (
          <>
            <p className="text-lg sm:text-xl font-bold text-teal-600 mt-2">
              {formatString(sm.timeRemaining, {
                time: `${timeRemaining}s`,
              })}
            </p>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">{sm.autoSubmitHint}</p>
          </>
        )}
        <p className="text-xs sm:text-sm text-gray-500 mt-1">
          {formatString(sm.submittedProgress, {
            submitted: String(submittedCount),
            total: String(participants.length),
          })}
        </p>
      </div>

      <div className="space-y-3">
        <textarea
          value={answer}
          disabled={isLockedAfterSubmit}
          onChange={(e) => setAnswer(e.target.value.slice(0, 200))}
          rows={4}
          maxLength={200}
          placeholder={sm.answerPlaceholder}
          className="w-full p-3 border-2 border-teal-300 rounded-xl focus:outline-none focus:border-teal-500 disabled:bg-gray-100"
        />
        <div className="flex items-center justify-between text-xs sm:text-sm text-gray-600">
          <span>{answer.length}/200</span>
          {hasSubmitted && !isEditingSubmitted && (
            <span className="text-green-600 font-semibold">{sm.submitted}</span>
          )}
        </div>
        {hasSubmitted && !isEditingSubmitted && (
          <button
            onClick={() => {
              setIsEditingSubmitted(true);
            }}
            className="w-full bg-white border-2 border-teal-300 text-teal-700 py-3 rounded-xl font-bold"
          >
            {sm.editAnswerButton}
          </button>
        )}
        {(!hasSubmitted || isEditingSubmitted) && (
          <button
            onClick={handleSubmit}
            disabled={answer.trim().length === 0}
            className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-3 rounded-xl font-bold disabled:from-gray-400 disabled:to-gray-400"
          >
            {hasSubmitted ? sm.resubmitAnswerButton : sm.submitAnswerButton}
          </button>
        )}
      </div>
    </div>
  );
};
